import {
  ComboboxPopover,
  ComboboxProvider,
  useComboboxStore,
} from '@ariakit/react';
import { Decimal } from 'decimal.js-light';
import type { FieldValidator } from 'formik';
import { useFormikContext } from 'formik';
import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  CSSProperties,
  FC,
  ReactNode,
} from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import mergeRefs from 'react-merge-refs';
import { ErrorMessage } from 'src/components/form/error-message';
import { useFieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import type { FormFieldProps } from 'src/components/form/form-field-props';
import { FormHint } from 'src/components/form/form-hint';
import { InnerAddon } from 'src/components/form/inner-addon';
import { FieldLabel } from 'src/components/form/label';
import {
  SearchableSelectInput,
  SearchableSingleSelectItem,
  comboboxPopoverClasses,
} from 'src/components/form/select/searchable-single-select';
import {
  formFieldBaseClasses,
  formFieldDisabledClasses,
  formFieldErrorClasses,
} from 'src/components/form/styles';
import { useCustomField } from 'src/components/form/use-custom-field';
import type { OnShowError } from 'src/components/form/use-error-handler';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import type { RectReadOnly } from 'src/hooks/use-measure';
import { useStorybook } from 'src/hooks/use-storybook';
import { cn } from 'src/utils/cn';
import type { StrictOmit } from 'src/utils/utility-types';

export function getInputClasses({
  error,
  className,
}: {
  error?: boolean;
  className?: string;
}) {
  return cn(
    formFieldBaseClasses,
    error && formFieldErrorClasses,
    formFieldDisabledClasses,
    className
  );
}

export type SimpleInputProps = {
  error?: boolean;
  leftBounds?: RectReadOnly;
  rightBounds?: RectReadOnly;
  as?: FC<ComponentPropsWithoutRef<'input'>>;
} & ComponentPropsWithRef<'input'>;

export const SimpleInput: FC<SimpleInputProps> = ({
  error,
  leftBounds,
  rightBounds,
  as,
  ...props
}) => {
  const Component = as ?? 'input';
  return (
    <Component
      {...props}
      style={
        {
          ...props.style,
          '--field-padding-left': leftBounds
            ? `${leftBounds.width}px`
            : undefined,
          '--field-padding-right': rightBounds
            ? `${rightBounds.width}px`
            : undefined,
        } as CSSProperties
      }
      className={cn(
        getInputClasses({ error, className: props.className }),
        rightBounds &&
          `
            pr-(--field-padding-right)
            legacy:pr-(--field-padding-right)
          `,
        leftBounds &&
          `
            pl-(--field-padding-left)
            legacy:pl-(--field-padding-left)
          `
      )}
    />
  );
};

export type InputProps = {
  stacked?: boolean;
  nested?: boolean;
  validate?: FieldValidator;
  onShowError?: OnShowError;
  outerRightAddon?: ReactNode;
  leftAddon?: ReactNode;
  unit?: string;
  /**
   * Renders a list of possible values for the user as a list of suggestions to make the selecting process easier.
   * If you don't want to allow arbitrary values, consider using a [Select](./?path=/docs/components-form-single-select--docs) instead.
   *
   * It's a good practice to hide the suggestions completely when the user entered a valid value or there's no meaningful suggestion.
   *
   * If you need to change the content of the suggestions popover use `renderSuggestions`.
   */
  suggestions?: (string | number)[];
  /**
   * Use this to change the content of the suggestions popover.
   */
  renderSuggestions?: (props: { children: ReactNode }) => ReactNode;
  /**
   * The `placeholder` should guide the user to provide valid input. Refer to the [placeholder pattern documentation](./?path=/docs/patterns-content-placeholders--docs) for more information.
   */
  placeholder?: string;
} & FormFieldProps &
  StrictOmit<ComponentPropsWithRef<'input'>, 'value'>;

/**
 * Use this component whenever a user needs to provide a shorter text input. It also works for providing numbers, passwords
 * and email addresses by customizing the `type` prop. For advanced use cases you can also use a hidden input field, if you
 * want to provide the user only an error message.
 *
 * For longer text input you can use [Textarea](./?path=/docs/components-form-textarea--docs).
 *
 * If the user needs to provide Markdown use [Editor](./?path=/docs/components-form-editor--docs) instead.
 *
 * For phone numbers use [Phone](./?path=/docs/components-form-phone--docs) or [Simple Phone](./?path=/docs/components-form-simple-phone--docs) instead.
 */
export const Input: FC<InputProps> = ({ ref, ...props }) => {
  const { isStacked, minTablet } = useDefaultStacked();

  const {
    label,
    name,
    stacked = isStacked,
    nested,
    validate,
    hint,
    onShowError,
    hideLabel = false,
    leftAddon,
    outerRightAddon,
    unit,
    info,
    suggestions,
    renderSuggestions = ({ children }) => children,
    markAsRequired,
    ...inputProps
  } = props;
  const id = useId();
  const { isDocsPage } = useStorybook();

  const inputRef = useRef<HTMLInputElement>(null);

  const [field, showError, error, helper] = useCustomField<
    string | number | null
  >(name, label, {
    validate(value) {
      if (inputRef.current?.type === 'number' && typeof value === 'number') {
        try {
          const isEqual = new Decimal(inputRef.current.value).equals(value);
          if (!isEqual) {
            if (inputRef.current.value.includes('.')) {
              return 'This number uses too many decimal places.';
            } else {
              return 'This number is too large.';
            }
          }
        } catch (err) {
          // likely DecimalError, because "inputRef.current.value" cannot be converted.
          // should be safe to ignore as other validations should kick in.
        }
      }
      return validate?.(value);
    },
    onShowError,
  });

  // when we have a schema like `z.number().nullable()`, we need to set the value to null when the user clears the input
  // as formik sets it back to an empty string.
  const originalOnChange = field.onChange;
  field.onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    originalOnChange(e);
    if (e.target.value === '') helper.setValue(null);
  };

  const { submitCount, setFieldValue } = useFormikContext();
  const [selectedValue, setSelectedValue] = useState<string>('');

  const store = useComboboxStore({
    selectedValue,
    setSelectedValue: (selectedValue) => {
      setSelectedValue(selectedValue);
      if (selectedValue === '') {
        helper.setValue(null);
      } else if (inputProps.type === 'number') {
        helper.setValue(Number(selectedValue));
      } else {
        helper.setValue(selectedValue);
      }
    },
    value: field.value?.toString() ?? '',
    setValue: (value) => {
      // formik is handling the value update, so we don't need
      // to do anything here
    },
  });

  useEffect(() => {
    if (inputProps.type === 'password') return;
    if (typeof field.value !== 'string') return;
    const trimmed = field.value.trim();
    if (trimmed === field.value) return;
    setFieldValue(name, trimmed);
    // oxlint-disable-next-line exhaustive-deps
  }, [submitCount]);

  const formGroup = useFieldGroup();
  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, name, formGroup]);

  const focusRef = useFocusManager();

  const fieldId = formGroup?.id || id;
  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;
  const ariaLabel = hideLabel ? displayLabel : undefined;

  const { disabledMessage, ...restInputProps } = inputProps;

  const fieldItem = (
    <FieldItem>
      <InnerAddon
        showError={showError}
        disabled={inputProps.disabled}
        left={leftAddon}
        right={
          unit ? (
            <small
              data-testid={`${field.name}Unit`}
              className="legacy:pt-[0.5rem]"
            >
              {unit}
            </small>
          ) : undefined
        }
      >
        {({ leftBounds, rightBounds }) => (
          <Tooltip
            content={
              inputProps.disabled && disabledMessage
                ? disabledMessage
                : undefined
            }
          >
            {(targetProps) => (
              <ComboboxProvider store={store}>
                <SimpleInput
                  as={suggestions ? SearchableSelectInput : undefined}
                  ref={mergeRefs([focusRef, inputRef, ref!])}
                  id={fieldId}
                  error={showError}
                  data-test-error={showError}
                  data-testid={field.name}
                  {...field}
                  aria-label={ariaLabel}
                  onWheel={(e: React.WheelEvent<HTMLInputElement>) =>
                    e.currentTarget.blur()
                  }
                  {...restInputProps}
                  // if we have multiple pages embedded in a docs view,
                  // we don't want autoFocus to interfere with Storybooks
                  // scroll position handling
                  autoFocus={isDocsPage ? false : props.autoFocus}
                  // if value is null, default to empty string
                  value={field.value ?? ''}
                  leftBounds={leftBounds}
                  rightBounds={rightBounds}
                  {...targetProps}
                />

                {Boolean(suggestions?.length) &&
                  createPortal(
                    <ComboboxPopover
                      className={comboboxPopoverClasses}
                      data-testid={`${name}-suggestions`}
                      unmountOnHide
                    >
                      {renderSuggestions({
                        children: suggestions?.map((suggestion) => (
                          <SearchableSingleSelectItem
                            key={suggestion}
                            data-testid={`${name}-suggestions-item-${suggestion}`}
                            value={suggestion.toString()}
                          />
                        )),
                      })}
                    </ComboboxPopover>,
                    document.body
                  )}
              </ComboboxProvider>
            )}
          </Tooltip>
        )}
      </InnerAddon>

      {hint && (
        <FormHint isDisabled={props.disabled} data-testid={`${field.name}Hint`}>
          {hint}
        </FormHint>
      )}

      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
        />
      )}
    </FieldItem>
  );

  const wrappedFieldItem = outerRightAddon ? (
    <div
      className={`
        grid grid-flow-col grid-cols-[1fr_auto] items-baseline gap-md
        legacy:gap-[1rem]
      `}
    >
      {fieldItem} {outerRightAddon}
    </div>
  ) : (
    fieldItem
  );

  return ((formGroup && !minTablet) || !formGroup) && !hideLabel ? (
    <FieldLayout stacked={stacked}>
      <FieldLabel
        name={name}
        displayLabel={displayLabel}
        fieldId={fieldId}
        info={info}
        showError={showError}
        nested={nested}
      />
      {wrappedFieldItem}
    </FieldLayout>
  ) : (
    wrappedFieldItem
  );
};
